import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import isNode from 'detect-node'
import { GoogleLogin } from '@react-oauth/google'
import { FacebookLoginClient } from '@greatsumini/react-facebook-login'

const FACEBOOK_SDK_VERSION = 'v21.0'
const FACEBOOK_SCOPE = 'public_profile,email'

// The backend only accepts a Google ID token (see SERVICE_GOOGLEJWT in
// api's SocialLoginService.php); the OAuth access token grant is dead code
// that always throws. Google's rendered button is the only supported way
// to get an ID token, so it's stacked here, invisibly, over our own
// button - real clicks land on Google's button while the user sees ours.
const OverlayContainer = styled.div`
  position: relative;
  width: 100%;
`

const GoogleOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0;

  & > div,
  & iframe {
    width: 100% !important;
    height: 100% !important;
  }
`

// Replacement for the archived react-social-login HOC. Injects
// `triggerLogin` and `loading` into the wrapped component, consuming
// `provider` ('facebook' | 'google'), `appId`, `onLoginSuccess` and
// `onLoginFailure`. Success is reported as `{ provider, token }`, where
// token.accessToken (facebook) or token.idToken (google) holds the
// provider credential.
//
// Some callers (GoogleLoginV2/GoogleLogin) already implement their own
// login flow and pass a `triggerLogin` prop in - matching
// react-social-login's original precedence, that external prop wins and
// nothing here is rendered on top of it.
const withSocialLogin = (WrappedComponent) => {
  const WithSocialLogin = ({
    appId,
    provider,
    onLoginSuccess = () => {},
    onLoginFailure = () => {},
    triggerLogin: externalTriggerLogin,
    ...rest
  }) => {
    const [loading, setLoading] = useState(false)

    useEffect(() => {
      if (provider === 'facebook' && appId && !externalTriggerLogin) {
        FacebookLoginClient.loadSdk('en_US').then(() => {
          FacebookLoginClient.init({
            appId,
            version: FACEBOOK_SDK_VERSION,
          })
        })
      }
    }, [provider, appId, externalTriggerLogin])

    const facebookLogin = useCallback(() => {
      FacebookLoginClient.login(
        (response) => {
          setLoading(false)
          if (response.authResponse) {
            onLoginSuccess({
              provider,
              token: { accessToken: response.authResponse.accessToken },
            })
          } else {
            onLoginFailure(response)
          }
        },
        { scope: FACEBOOK_SCOPE },
      )
    }, [provider, onLoginSuccess, onLoginFailure])

    const ownTriggerLogin = useCallback(() => {
      if (provider !== 'facebook') return
      setLoading(true)
      facebookLogin()
    }, [provider, facebookLogin])

    const handleGoogleSuccess = useCallback(
      (credentialResponse) => {
        setLoading(false)
        onLoginSuccess({
          provider,
          token: { idToken: credentialResponse.credential },
        })
      },
      [provider, onLoginSuccess],
    )

    const handleGoogleError = useCallback(() => {
      setLoading(false)
      onLoginFailure(new Error('Google login failed'))
    }, [onLoginFailure])

    if (externalTriggerLogin) {
      return (
        <WrappedComponent
          {...rest}
          triggerLogin={externalTriggerLogin}
          loading={loading}
        />
      )
    }

    if (provider === 'google') {
      return (
        <OverlayContainer onClick={() => setLoading(true)}>
          <WrappedComponent
            {...rest}
            triggerLogin={ownTriggerLogin}
            loading={loading}
          />
          {/* GoogleLogin reads GoogleOAuthProvider's context via a hook,
          which isn't safe under the SSR tree-walk (see App.js) - render
          it client-side only. */}
          {!isNode && appId && (
            <GoogleOverlay>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
              />
            </GoogleOverlay>
          )}
        </OverlayContainer>
      )
    }

    return (
      <WrappedComponent
        {...rest}
        triggerLogin={ownTriggerLogin}
        loading={loading}
      />
    )
  }

  return WithSocialLogin
}

export default withSocialLogin
